import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import InputTextArea from 'modules/_shared/components/InputTextArea';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';

import { IncentiveOfferOperationComment } from 'modules/incentivesMarket/types';
import asyncActionSwal from 'utils/asyncActionSwal';

import { useIncentiveOfferOperationDetail } from '../context/IncentiveOfferOperationDetailContext';

import IncentiveOfferOperationDetailCommentsItem from './IncentiveOfferOperationDetailCommentsItem';

const IncentiveOfferOperationDetailComments: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useIncentiveOfferOperationDetail();
  const [draft, setDraft] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const sortedComments = useMemo(() => {
    const list = data.operation?.comments || [];
    return [...list].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [data.operation?.comments]);

  const handleSubmit = useCallback(async () => {
    const text = draft.trim();
    if (!text || !data.canPostComment) return;
    setIsSubmitting(true);
    try {
      await actions.handleAddComment(text);
      setDraft('');
    } finally {
      setIsSubmitting(false);
    }
  }, [draft, data.canPostComment, actions]);

  const handleRemove = useCallback(
    (comment: IncentiveOfferOperationComment) => {
      if (!data.isAdmin) return;
      const raw = comment.text?.trim() || '';
      const preview =
        raw.slice(0, 100) + (raw.length > 100 ? '…' : '');
      asyncActionSwal({
        swalTitle: t('DeleteOperationReviewSwalTitle'),
        swalElementName: preview || '—',
        swalInfoText: t('DeleteOperationReviewSwalInfo'),
        swalConfirmButtonText: t('OkDelete'),
        callback: async () => {
          setRemovingId(comment._id);
          try {
            await actions.handleRemoveComment(comment._id);
          } finally {
            setRemovingId(null);
          }
        },
      });
    },
    [data.isAdmin, actions, t]
  );

  return (
    <div className="nk-block">
      <div className="card card-bordered">
        <div className="card-inner">
          <h6 className="card-title mb-3">
            <em className="icon ni ni-comments text-primary mr-2" />
            {t('Comments')}
          </h6>

          {data.canPostComment ? (
            <div className="mb-4">
              <InputTextArea
                id="trading-offer-operation-review"
                label=""
                placeholder={t('OperationReviewPlaceholder')}
                value={draft}
                onChange={setDraft}
                rows={4}
                maxLength={3000}
                disabled={data.isLoading || isSubmitting}
              />
              <div className="text-right mt-2">
                <Button
                  variant={ButtonVariants.PRIMARY}
                  dimStyle
                  text={t('PostOperationReview')}
                  disabled={
                    data.isLoading || isSubmitting || !draft.trim().length
                  }
                  onClick={handleSubmit}
                />
              </div>
            </div>
          ) : null}

          {sortedComments.length ? (
            <ul className="list-unstyled mb-0">
              {sortedComments.map((comment) => (
                <IncentiveOfferOperationDetailCommentsItem
                  key={comment._id}
                  comment={comment}
                  isAdmin={data.isAdmin}
                  isLoading={data.isLoading}
                  isRemoving={removingId === comment._id}
                  onRemove={handleRemove}
                />
              ))}
            </ul>
          ) : (
            <p className="text-soft fs-13px mb-0">{t('NoComments')}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default IncentiveOfferOperationDetailComments;
